import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ComplydomeComponent } from './complydome/complydome.component';
import { LoginComponent } from './auth/login/login.component';
import { AuthModule } from './auth/auth.module';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';
import { PasswordResetSentComponent } from './auth/password-reset-sent/password-reset-sent.component';
import { PasswordRecoveryComponent } from './auth/password-recovery/password-recovery.component';
import { MfaComponent } from './auth/mfa/mfa.component';
import { UnauthLayoutComponent } from './auth/unauth-layout/unauth-layout.component';
import { StopInactivityServiceGuard } from './auth/stop-inactivity-service-guard.service';
import { AuthGuardService } from './auth/auth-guard.service';

const routes: Routes = [
  {
    path: '',
    component: UnauthLayoutComponent,
    children: [
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      { path: 'login', component: LoginComponent, canActivate: [StopInactivityServiceGuard] },
      { path: 'forgot-password', component: ForgotPasswordComponent, canActivate: [StopInactivityServiceGuard] },
      { path: 'mfa', component: MfaComponent },
      { path: 'password-reset-sent', component: PasswordResetSentComponent, canActivate: [StopInactivityServiceGuard] },
      { path: 'pwd-recovery', component: PasswordRecoveryComponent, canActivate: [StopInactivityServiceGuard] },
    ]
  },
  { path: 'guest', loadChildren: () => import('./guest/guest.module').then(m => m.GuestModule) },

  // Full-screen course player — deliberately a top-level sibling of `shell`,
  // not nested under its `component: ComplydomeComponent` children, so it
  // never inherits the shell's sidebar/header/footer chrome and can take
  // over the whole viewport like the koursebox-widgets player mock it's
  // based on. The multi-segment path below just makes the URL *read* as if
  // it lived under My Compliance's Trainings tab — it isn't actually a child
  // of the 'shell' route above.
  //
  // Real Angular components per training-content element type (quiz, cards,
  // document, profile, scenario, recall, text, image), backed end-to-end by
  // the real Koursebox player/exam/certificate API — see plan at
  // C:\Users\josantos\.claude\plans\excelente-se-requiere-crear-inherited-sutherland.md
  // for the design rationale (a since-removed earlier engine informed the
  // build order but no longer exists in this codebase).
  {
    path: 'shell/entity-management/my-compliance/trainings/course-player',
    loadChildren: () => import('./features/course-player/course-player.module').then(m => m.CoursePlayerModule),
    canActivate: [AuthGuardService],
  },

  // Stripe returns Koursebox purchases to these full-screen views. Keeping
  // them outside the shell avoids briefly rendering the assignment UI while
  // the customer reads the checkout result.
  {
    path: 'koursebox/purchase',
    loadChildren: () => import('./features/koursebox/purchase-return/koursebox-purchase-return.module').then(m => m.KourseboxPurchaseReturnModule),
    canActivate: [AuthGuardService],
  },

  {
    path: 'shell',
    component: ComplydomeComponent,
    canActivate: [AuthGuardService],
    children: [
      { path: 'user-management', loadChildren: () => import('./features/user-management/user-management.module').then(m => m.UserManagementModule) },
      { path: 'client-management', loadChildren: () => import('./features/client-management/client-management.module').then(m => m.ClientManagementModule) },
      { path: 'compliance-management', loadChildren: () => import('./features/compliance-management/compliance-management.module').then(m => m.ComplianceManagementModule) },
      // { path: 'financial-compliance', loadChildren: () => import('./features/financial-compliance/financial-compliance.module').then(m => m.FinancialComplianceModule) },
      { path: 'entity-management', loadChildren: () => import('./features/entity-management/entity-management.module').then(m => m.EntityManagementModule) },
      { path: 'policy-management', loadChildren: () => import('./features/policy-management/policy-management.module').then(m => m.PolicyManagementModule) },
      { path: 'dashboard', loadChildren: () => import('./features/dashboard/dashboard.module').then(m => m.DashboardModule) },
      { path: 'audit-management', loadChildren: () => import('./features/audit-management/audit-management.module').then(m => m.AuditManagementModule) },
      { path: 'koursebox', loadChildren: () => import('./features/koursebox/koursebox.module').then(m => m.KourseboxModule) },
      // { path: 'risk-assessment', loadChildren: () => import('./features/risk-assessment/risk-assessment.module').then(m => m.RiskAssessmentModule) },
      // { path: '', redirectTo: 'user-management', pathMatch: 'full' }, // Default route
      { path: 'reports', loadChildren: () => import('./features/reports/reports.module').then(m => m.ReportsModule) },

      { path: '', redirectTo: 'dashboard', pathMatch: 'full' }, // Default route
    ]
  },
  // { path: '**', redirectTo: '' } // Wildcard route for handling 404 pages
];

@NgModule({
  imports: [RouterModule.forRoot(routes), AuthModule],
  exports: [RouterModule]
})
export class AppRoutingModule { }
